import mqtt from 'mqtt'

const CONNECT_TIMEOUT_MS = 10000

/*
 * Thin wrapper around mqtt.js for chat-change notifications.
 *
 * Topic pattern: `{topicBase}/{hashhashDomain}/chat/{connectionKey}`.
 * Example: `tibtix/dev/events/eisenzentrum.org/chat/b98b54b8-...`
 * Payload is JSON with { connectionKey, version, chatTextVersion,
 * clientLastViewed, domainLastViewed, clientUnreadCount, domainUnreadCount, chatSize }.
 *
 * We subscribe with a wildcard at the connectionKey segment so one subscription per
 * distinct hashhashDomain covers all connections on that domain. Scheme is derived
 * from window.location (wss on https, ws on http).
 *
 * Status lifecycle (consumers register via onStatus):
 *   idle → connecting → connected
 *                     ↘ failed   (timeout or unusable config)
 *   connected → connecting (broker dropped, mqtt.js auto-retrying)
 *             ↘ failed     (still not back after CONNECT_TIMEOUT_MS)
 * On 'failed' the underlying mqtt.js client keeps retrying; consumers should
 * fall back to polling but stay subscribed — a successful reconnect flips
 * status back to 'connected'.
 */
export default class ChatMqttClient {
  constructor(config) {
    this.config = config
    this.client = null
    this.messageListeners = []
    this.statusListeners = []
    this.status = 'idle'
    this.connectTimeoutHandle = null
  }

  isUsable() {
    if (!this.config || !this.config.configured || !this.config.topicBase) return false
    return !!(this.config.wsUrl || (this.config.host && (this.config.wsPort || this.config.port)))
  }

  resolveUrl() {
    if (this.config.wsUrl) return this.config.wsUrl
    const scheme = this.config.wsProtocol
        || (window.location.protocol === 'https:' ? 'wss' : 'ws')
    const port = this.config.wsPort || this.config.port
    return `${scheme}://${this.config.host}:${port}`
  }

  setStatus(s) {
    if (this.status === s) return
    this.status = s
    for (const fn of this.statusListeners) {
      try { fn(s) } catch (e) { /* ignore */ }
    }
  }

  armConnectTimeout() {
    this.clearConnectTimeout()
    this.connectTimeoutHandle = setTimeout(() => {
      this.connectTimeoutHandle = null
      if (this.status !== 'connected') this.setStatus('failed')
    }, CONNECT_TIMEOUT_MS)
  }

  clearConnectTimeout() {
    if (this.connectTimeoutHandle) {
      clearTimeout(this.connectTimeoutHandle)
      this.connectTimeoutHandle = null
    }
  }

  connect() {
    if (!this.isUsable()) {
      this.setStatus('failed')
      return
    }
    const url = this.resolveUrl()
    this.setStatus('connecting')
    this.armConnectTimeout()
    try {
      this.client = mqtt.connect(url, {
        keepalive: 30,
        reconnectPeriod: 5000,
        clean: true
      })
      this.client.on('connect', () => {
        this.clearConnectTimeout()
        this.setStatus('connected')
      })
      this.client.on('reconnect', () => {
        if (this.status === 'connected') {
          this.setStatus('connecting')
          this.armConnectTimeout()
        }
      })
      this.client.on('close', () => {
        if (this.status === 'connected') {
          this.setStatus('connecting')
          this.armConnectTimeout()
        }
      })
      this.client.on('offline', () => {
        if (this.status === 'connected') {
          this.setStatus('connecting')
          this.armConnectTimeout()
        }
      })
      this.client.on('message', (topic, payload) => {
        for (const fn of this.messageListeners) {
          try { fn(topic, payload) } catch (e) { /* ignore */ }
        }
      })
      this.client.on('error', () => { /* swallow — reconnect handles it */ })
    } catch (e) {
      this.client = null
      this.setStatus('failed')
    }
  }

  /* Subscribe to the chat wildcard for one hashhashDomain. */
  subscribeForDomain(hashhashDomain) {
    if (!this.client || !hashhashDomain) return
    const topic = `${this.config.topicBase}/${hashhashDomain}/chat/+`
    this.client.subscribe(topic)
  }

  /* Blinklicht: auf eine feste Liste bedeutungsloser Topics abonnieren (keine Wildcards). */
  subscribeExact(topics) {
    if (!this.client || !Array.isArray(topics)) return
    for (const t of topics) {
      if (t) this.client.subscribe(t)
    }
  }

  onMessage(fn) {
    this.messageListeners.push(fn)
  }

  /* Register a status listener; receives the current status immediately. */
  onStatus(fn) {
    this.statusListeners.push(fn)
    try { fn(this.status) } catch (e) { /* ignore */ }
  }

  disconnect() {
    this.clearConnectTimeout()
    if (this.client) {
      try { this.client.end(true) } catch (e) { /* ignore */ }
      this.client = null
    }
    this.messageListeners = []
    this.statusListeners = []
    this.status = 'idle'
  }

  /* Extracts the {connectionKey} segment from a received topic. */
  static parseConnectionKey(topic) {
    if (!topic) return null
    const parts = topic.split('/')
    return parts[parts.length - 1] || null
  }
}